import axios from 'axios';
import { CLOTHING_CATEGORIES } from '@/constants/clothingCategories';
import type { BoundingBox } from '@/types/BoundingBox.types';
import { updatedImageUrl } from '@/utils/formatting';
import type {
  GenerateBoundingBoxesOptions,
  OnVisionErrorCallback,
  OnVisionSuccessCallback,
  VisionAPIResponse,
} from './types';

const GOOGLE_VISION_API_KEY = import.meta.env.VITE_GOOGLE_VISION_API_KEY;

export const generateBoundingBoxes = async (
  imageUrl: string,
  includePerson: boolean = false,
  onSuccess: OnVisionSuccessCallback,
  onError: OnVisionErrorCallback,
  options: GenerateBoundingBoxesOptions = {}
): Promise<void> => {
  const { frameIndex = null } = options;

  try {
    const response = await fetch(updatedImageUrl(imageUrl));
    const blob = await response.blob();

    const reader = new FileReader();
    reader.readAsDataURL(blob);

    reader.onloadend = async () => {
      try {
        if (!reader || !reader.result || typeof reader.result !== 'string') {
          throw new Error('error reading image data');
        }

        const base64data = reader.result.split(',')[1];

        const visionResponse = await axios.post<VisionAPIResponse>(
          `https://vision.googleapis.com/v1/images:annotate?key=${GOOGLE_VISION_API_KEY}`,
          {
            requests: [
              {
                image: { content: base64data },
                features: [{ type: 'OBJECT_LOCALIZATION', maxResults: 10 }],
              },
            ],
          }
        );

        if (
          !visionResponse ||
          !visionResponse.data ||
          !visionResponse.data.responses ||
          !Array.isArray(visionResponse.data.responses) ||
          !visionResponse.data.responses[0].localizedObjectAnnotations
        )
          throw new Error('invalid data response');

        const objects =
          visionResponse.data.responses[0].localizedObjectAnnotations;

        // First, filter objects based on categories
        const filteredObjects = objects.filter((obj) => {
          if (!includePerson && obj.name.toLowerCase() === 'person')
            return false;

          const objLower = obj.name.toLowerCase();
          return includePerson
            ? objLower === 'person' ||
                CLOTHING_CATEGORIES.some((category) =>
                  objLower.includes(category)
                )
            : CLOTHING_CATEGORIES.some((category) =>
                objLower.includes(category)
              );
        });

        // Group objects by their coordinates to detect duplicates
        const coordinateGroups: Record<string, typeof objects> = {};
        filteredObjects.forEach((obj) => {
          // Create a unique key from coordinates (rounded to reduce floating point issues)
          const coordKey = [
            obj.boundingPoly.normalizedVertices[0].x.toFixed(5),
            obj.boundingPoly.normalizedVertices[0].y.toFixed(5),
            obj.boundingPoly.normalizedVertices[2].x.toFixed(5),
            obj.boundingPoly.normalizedVertices[2].y.toFixed(5),
          ].join(',');

          if (!coordinateGroups[coordKey]) {
            coordinateGroups[coordKey] = [];
          }
          coordinateGroups[coordKey].push(obj);
        });

        // For each coordinate group, keep only the object with highest confidence
        const deduplicatedObjects = Object.values(coordinateGroups).map(
          (group) => {
            // Sort by confidence score in descending order
            group.sort((a, b) => b.score - a.score);
            return group[0]; // Return the highest confidence object
          }
        );

        // Now create unique labels for the deduplicated objects
        const labelCounts: Record<string, number> = {};
        const filteredBoxes: BoundingBox[] = deduplicatedObjects.map((obj) => {
          const baseName = obj.name;
          labelCounts[baseName] = (labelCounts[baseName] || 0) + 1;

          // Create unique label only if we have true duplicates (same name, different coordinates)
          const uniqueLabel =
            labelCounts[baseName] > 1
              ? `${baseName} ${labelCounts[baseName]}`
              : baseName;

          const boxWidth =
            obj.boundingPoly.normalizedVertices[2].x -
            obj.boundingPoly.normalizedVertices[0].x;
          const boxHeight =
            obj.boundingPoly.normalizedVertices[2].y -
            obj.boundingPoly.normalizedVertices[0].y;

          const box: BoundingBox = {
            label: uniqueLabel,
            baseLabel: baseName,
            score: obj.score,
            x: obj.boundingPoly.normalizedVertices[0].x,
            y: obj.boundingPoly.normalizedVertices[0].y,
            width: boxWidth,
            height: boxHeight,
            area: boxWidth * boxHeight,
          };

          // Add frameIndex if provided
          if (frameIndex !== null) {
            box.frameIndex = frameIndex;
          }

          return box;
        });

        // Calculate the largest bounding box excluding person if requested
        let largestBoxIndex = 0;
        let largestArea = 0;

        // Find non-person boxes if needed
        const boxesToConsider = !includePerson
          ? filteredBoxes.filter(
              (box) => box.baseLabel.toLowerCase() !== 'person'
            )
          : filteredBoxes;

        // If we have any boxes to consider, find the largest
        if (boxesToConsider.length > 0) {
          boxesToConsider.forEach((box) => {
            if (box.area > largestArea) {
              largestArea = box.area;
              largestBoxIndex = filteredBoxes.indexOf(box); // Get index in original array
            }
          });
        }

        // Execute success callback with results
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(filteredBoxes, largestBoxIndex);
        }
      } catch (error) {
        console.error('Error processing Vision API response:', error);
        onError(error instanceof Error ? error : new Error('Unknown error'));
      }
    };
  } catch (error) {
    console.error('Error analyzing image:', error);
    onError(error instanceof Error ? error : new Error('Unknown error'));
  }
};

const visionAPI = {
  generateBoundingBoxes,
};

export default visionAPI;
